<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container } from '@Shelly/shelly-design-system';
	import { devices_status } from 'src/stores/device-runtime/status';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { onMount } from 'svelte';
	import { create_control } from '../../helpers/ui_to_temp_scheme';
	import { capFirstChar, _deepClone, _deepCopyArr } from '../../../../helpers/utilities';
	import { real_id } from '../../../../helpers/interface_helpers';
	import type { ITempSchemeObj } from '../../../../types/TemperatureSchemeType';
	import { extractHexId } from '../../helpers/temp_scheme_to_ui';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	import type { Room } from 'src/components/SelectList/SelectList.types';
	import { defaultHiddenDevicesRoom } from 'src/helpers/constants';
	import EmptyDeviceTypeList from 'src/components/Lists/EmptyDeviceTypeList.svelte';
	import { isTestUser } from 'src/helpers/user_helpers';
	import { HiddenDevicesRoomID, SharedRecentlyDevicesRoomID } from 'src/helpers/service_rooms_helpers';
	import { sortRoomsWithMissingDevices } from 'src/components/Lists/list-helpers';
	import { isEmeterWithRelay } from 'src/helpers/interface_helpers';

	$: statusSnapshot = $devices_status;
	export let allRelaysRefs, selectedRelay, temp_scheme_object: ITempSchemeObj;

	let selectedItems = [];

	$: if (temp_scheme_object?.tocool?.blk.length > 0) {
		selectedItems = [
			...temp_scheme_object?.tocool?.blk.map((dev) => {
				let hexId = extractHexId(dev?.r || '');
				return $devices[hexId] || { id: hexId, name: 'deleted' };
			}),
		].filter((el) => el !== undefined);
	} else {
		selectedItems = [];
	}

	$: {
		selectedRelay =
			selectedItems.length > 0 ? selectedItems[0] : { id: undefined, value: undefined };
	}

	const filterDevicesByRoomID: any = (devices: any) => {
		//only devices from user account in rooms passed here
		let filteredDevicesByRoom = {};

		devices.forEach((device: any, i) => {
			const roomID = device.room_id;

			const allRooms = {
				...$rooms,
				...(isTestUser() && {
					[defaultHiddenDevicesRoom.value as number]: {
						name: $format(defaultHiddenDevicesRoom.title, {
							default: defaultHiddenDevicesRoom.translationDefault,
						}),
						id: defaultHiddenDevicesRoom.value,
					},
				}),
			};

			const deviceData = {
				name: `${device.name}${
					!statusSnapshot?.[real_id(device.id)]?.online
						? `- ${capFirstChar($format('label_offline'))}`
						: ''
				}`,
				id: device.id,
				selected: selectedItems.find((el) => el?.id === device?.id) ? true : false,
				roomName: allRooms[roomID]?.name || 'Unknown room',
				roomID: roomID,
				image: device.image,
				backgroundColor: device?.backgroundColor,
			};

			if (filteredDevicesByRoom[roomID]) {
				filteredDevicesByRoom[roomID] = {
					cards: [...filteredDevicesByRoom[roomID].cards, deviceData],
					roomName: allRooms[roomID].name || 'Unknown room',
					roomID: roomID,
				};
			} else {
				filteredDevicesByRoom[roomID] = {
					cards: [deviceData],
					roomName: allRooms[roomID]?.name || 'Unknown room',
					roomID: roomID,
				};
			}
		});

		let missingDevices = selectedItems.filter(
			(selected) => !devices.some((device) => device.id === selected.id),
		);

		if (missingDevices.length > 0 && devices.length) {
			filteredDevicesByRoom['missing_devices'] = {
				cards: missingDevices.map((item) => ({
					name: item.id,
					id: item.id,
					roomID: 'missing_devices',
					selected: true,
					icon: 'text-warning fas fa-warning',
				})),
				roomName: $format('missing_devices_label', {
					default: 'Missing Devices',
				}),
				roomID: 'missing_devices',
			};
		}

		return sortRoomsWithMissingDevices(filteredDevicesByRoom);
	};

	const filterControls = (devices) => {
		let filtered = [];
		devices.forEach((d) => {
			if (
				!d.shared &&
				(isTestUser() || d.room_id !== HiddenDevicesRoomID) &&
				d.room_id != `${SharedRecentlyDevicesRoomID}` &&
				(/^(relay|valve)$/.test(d.category) ||
					// module in 'inputs_reader' and 'shell' mode doesn't have control components
					(d.category === 'module' && !/^(inputs_reader|shell)$/.test(d.mode)) ||
					isEmeterWithRelay(d))
			) {
				filtered.push(d);
			}
		});
		// console.log('FILTERED ', filtered);
		return filtered;
	};

	let relays = [];

	let roomsFiltered: Room[];
	$: roomsFiltered = (statusSnapshot, filterDevicesByRoomID(relays));

	const handleSelectItem = (id: string | number) => {
		let isSelected = false;

		if (selectedItems.find((item) => item.id == id) !== undefined) {
			selectedItems = selectedItems.filter((item) => item.id !== id);
		} else {
			isSelected = true;

			selectedItems = [
				...selectedItems,
				{
					id: id,
				},
			];
		}

		let roomIndex = -1;
		let cardIndex = -1;
		roomsFiltered.every((room: any, index) => {
			roomIndex = index;
			cardIndex = room.cards.findIndex((card) => {
				return card.id === id;
			});

			if (cardIndex !== -1) {
				return false;
			}

			return true;
		});

		roomsFiltered[roomIndex].cards[cardIndex].selected = isSelected;
	};

	const handle_controls_change = () => {
		// console.log('selected items ', selectedItems);
		let { toheat, tocool } = create_control({
			control: { type: 'relay' },
			data: {
				selectedItems,
				type: temp_scheme_object?._meta?.type,
				default_output: temp_scheme_object?._meta?.default_output,
			},
		});

		// let _copy: ITempSchemeObj = _deepClone(
		// 	$temp_scheme_object
		// ) as ITempSchemeObj;
		// _copy.toheat.blk = [...toheat.blk];
		// _copy.tocool.blk = [...tocool.blk];
		temp_scheme_object.toheat.blk = [...toheat.blk];
		temp_scheme_object.tocool.blk = [...tocool.blk];

		//In between comments whole meta data shit

		//TODO: drop it when shelly-app-2 is deprecated
		let data = [];
		if (temp_scheme_object?._meta?.data?.find((el) => el.sensor_id)) {
			data.push(temp_scheme_object._meta?.data?.find((el) => el.sensor_id));
		}
		if (selectedItems.length > 0) {
			selectedItems.forEach((el) => {
				data.push({ relay_id: el.id });
			});
		}

		// _copy._meta['data'] = _deepCopyArr(data);
		temp_scheme_object._meta['data'] = _deepCopyArr(data);
		//end it here

		// temp_scheme_object.change(_copy);
		console.log('updated temp scheme ', JSON.stringify(temp_scheme_object._meta.data));
	};
	onMount(() => {
		relays = [...filterControls(Object.values($devices))];
	});
</script>

<Container padding="scrollPaddings">
	{#if roomsFiltered.length > 0}
		<!-- DO NOT REMOVE COMMENT: Padding references -->
		<div class="pt-s"></div>
		<SelectList
			missingDevicesAlertHeading={$format('missing_devices_alert_label ', {
				values: {
					type: $format('device_type_thermostat', { default: 'thermostat' }),
				},
				default: 'You have missing device(s) in this {type}.',
			})}
			title={$format('label_selected_actuators', {
				default: 'Selected actuators:',
			})}
			titleHighlight={selectedItems.length}
			list={roomsFiltered}
			bind:allCardsRefs={allRelaysRefs}
			on:click={(e) => {
				handleSelectItem(e.detail.id);
				handle_controls_change();
			}}
		/>
		<!-- DO NOT REMOVE COMMENT: Padding references -->
		<div class="pb-s"></div>
	{:else}
		<EmptyDeviceTypeList type="modal" withoutMargin />
	{/if}
</Container>
